import { useCallback, useEffect, useState } from 'react';

/**
 * Column widths state: { columnKey: width }
 */
type ColumnWidths = Record<string, number>;

/**
 * Hook to manage resizable column widths.
 * Handles multiple columns but currently only title column is configured as resizable.
 */
export const useResizableColumns = (isResizableEnabled: boolean) => {
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>({});

  // Get width for a specific column
  const getWidth = useCallback(
    (columnKey: string): number | null => {
      return columnWidths[columnKey] ?? null;
    },
    [columnWidths]
  );

  // Update width for a specific column
  const setWidth = useCallback((columnKey: string, width: number) => {
    setColumnWidths((prev) => ({ ...prev, [columnKey]: width }));
  }, []);

  // Handle resize event from Table component (receives columnIndex)
  const handleResize = useCallback(
    (columnIndex: number, width: number, columns: Array<{ key?: unknown }>) => {
      const column = columns[columnIndex];
      if (column?.key && typeof column.key === 'string') {
        setWidth(column.key, width);
      }
    },
    [setWidth]
  );

  // Reset widths when feature is disabled
  useEffect(() => {
    if (!isResizableEnabled) {
      setColumnWidths({});
    }
  }, [isResizableEnabled]);

  return { columnWidths, getWidth, setWidth, handleResize };
};
